import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import CanonicalCardImage from "@/components/CanonicalCardImage";
import api, { downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Download, ArrowRight, KeyRound, Copy, Eye, EyeOff } from "lucide-react";

export default function Success() {
  const { memberId } = useParams();
  const [membership, setMembership] = useState(null);
  const [creds, setCreds] = useState(null);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    api.get(`/verify/${memberId}`).then((r) => {
      setMembership({ ...r.data, issued_at: new Date().toISOString() });
    });
    const stored = sessionStorage.getItem(`npc_creds_${memberId}`);
    if (stored) setCreds(JSON.parse(stored));
  }, [memberId]);

  const downloadCard = async () => {
    if (!membership?.membership_id) {
      toast.error("Card not ready yet");
      return;
    }
    try {
      await downloadFile(
        `/memberships/by-id/${membership.membership_id}/card.png`,
        `NPC-Card-${membership.member_id || membership.membership_id}.png`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not download card");
    }
  };
  const copy = async (txt) => {
    // navigator.clipboard requires HTTPS + a focused document. Fall back to
    // the legacy execCommand path when clipboard API is unavailable (older
    // mobile browsers, iframes, etc.) so the button never silently fails.
    if (!txt) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(txt);
        toast.success("Copied");
        return;
      }
    } catch (_) { /* fall through */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = txt;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) toast.success("Copied");
      else toast.error("Copy failed — long-press to select manually");
    } catch (e) {
      toast.error("Copy failed — long-press to select manually");
    }
  };

  return (
    <SiteLayout>
      <section className="mx-auto max-w-5xl px-6 lg:px-10 py-16 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1F7A3D]/10 text-[#1F7A3D] text-sm font-semibold">
          <CheckCircle2 className="h-4 w-4" /> Payment successful
        </div>
        <h1 className="mt-4 font-display text-5xl font-bold text-[#0F2540]" data-testid="success-title">
          Welcome to Nirmal Hospital{membership ? `, ${membership.full_name}` : ""}.
        </h1>
        <p className="mt-3 text-[#5C6B7A]">Your digital membership card has been generated. A copy has been sent to your email.</p>

        <div className="mt-10 flex justify-center">
          <CanonicalCardImage
            membershipId={membership?.membership_id}
            testId="success-membership-card"
          />
        </div>

        {creds && (
          <div className="mt-10 max-w-xl mx-auto rounded-3xl border-2 border-[#A53A2A]/30 bg-gradient-to-br from-[#FFF8F2] to-white p-6 lg:p-7 text-left shadow-[0_24px_60px_-30px_rgba(165,58,42,0.35)]" data-testid="auto-creds-block">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-[#A53A2A] text-white grid place-items-center"><KeyRound className="h-4 w-4" /></div>
              <h3 className="font-display text-xl font-bold text-[#0F2540]">
                {creds.is_existing_user ? "Your account is already set up" : "Your member login is ready"}
              </h3>
            </div>
            <p className="mt-2 text-sm text-[#5C6B7A]">
              {creds.is_existing_user
                ? "We linked this membership to your existing dashboard account. Log in with the password you already use — or use \"Forgot password?\" if you need to reset it."
                : "We've created your dashboard account. Save these one-time credentials — please change the password after your first login."}
            </p>
            <div className="mt-5 space-y-3">
              <div className="rounded-xl bg-white border border-[#E1E8F0] px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B7A]">Email</div>
                  <div className="font-mono text-sm text-[#0F2540] truncate" data-testid="auto-creds-email">{creds.email}</div>
                </div>
                <button onClick={() => copy(creds.email)} className="text-[#1E4F87] hover:bg-[#EAF1FA] p-2 rounded-lg" aria-label="Copy email" data-testid="copy-email-btn"><Copy className="h-4 w-4" /></button>
              </div>
              {creds.password ? (
                <div className="rounded-xl bg-white border border-[#E1E8F0] px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B7A]">Temporary password</div>
                    <div className="font-mono text-sm text-[#0F2540] truncate" data-testid="auto-creds-password">{showPw ? creds.password : "•".repeat(creds.password.length)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setShowPw((s) => !s)} className="text-[#1E4F87] hover:bg-[#EAF1FA] p-2 rounded-lg" aria-label="Toggle password" data-testid="toggle-pw-btn">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button onClick={() => copy(creds.password)} className="text-[#1E4F87] hover:bg-[#EAF1FA] p-2 rounded-lg" aria-label="Copy password" data-testid="copy-pw-btn"><Copy className="h-4 w-4" /></button>
                  </div>
                </div>
              ) : (
                <Link to="/forgot-password" className="block rounded-xl bg-[#EAF1FA] border border-[#C8D6E5] px-4 py-3 text-sm text-[#1E4F87] font-semibold hover:bg-[#DCE8F5] transition" data-testid="forgot-from-success">
                  Forgot your password? Reset it here →
                </Link>
              )}
            </div>
            <Link to={`/login?email=${encodeURIComponent(creds.email)}`} className="btn-primary w-full justify-center mt-5" data-testid="auto-creds-login-btn">
              Log in to my dashboard <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <button onClick={downloadCard} className="btn-primary" data-testid="download-card-btn">
            <Download className="h-4 w-4" /> Download card (PNG)
          </button>
          {!creds && (
            <Link to="/dashboard" className="btn-ghost" data-testid="go-dashboard-btn">
              Go to dashboard <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
